import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { createSelector, Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { filter, map, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import {
  addVehicleBindQuery,
  addVehicleInfoQuery,
  typeOfAgentEmail
} from '@amfam/policy/add-vehicle/data-access';
import { PolicySelectors } from '@amfam/policy/data-access';
import { PolicyAnalytics } from '@amfam/policy/models';
import { VehicleFacade, vehicleQuery } from '@amfam/policy/vehicle/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { userQuery } from '@amfam/shared/user';
import { agentQuery } from '@amfam/shared/utility/agent';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions, routerQuery } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { ConfirmationModel, DockingBarService, LoadingSpinnerService } from '@amfam/ui-kit';

import * as VehicleInfoActions from '../+state/add-vehicle-feature.actions';
import { addVehicleFeatureQuery } from '../+state/add-vehicle-feature.selectors';

@Component({
  selector: 'ds-vehicle-agent-notification-confirmation',
  templateUrl: './vehicle-agent-notification-confirmation.component.html',
  styleUrls: ['./vehicle-agent-notification-confirmation.component.scss'],
  standalone: false
})
export class VehicleAgentNotificationConfirmationComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  public policyNumber = '';
  public vehicleName = '';
  public classicOnlyAutoPolicyOrCompOnlyAdv = false;
  public isNewVehicle = true;
  public agents;
  public newExperience: boolean;

  public confirmationData$: Observable<ConfirmationModel> = this.store
    .select(addVehicleFeatureQuery.selectCurrentVehicleInfo)
    .pipe(
      withLatestFrom(
        this.store.select(userQuery.selectPrimaryEmail),
        this.store.select(userQuery.selectMyAccountEmail),
        this.store.select(BrandSelectors.selectIsPartner)
      ),
      take(1),
      tap(([vehicleStateInfo]) => {
        if (vehicleStateInfo) {
          if (
            vehicleStateInfo.typeOfAgentEmail === typeOfAgentEmail.vehicleBoundNeedsTPI ||
            vehicleStateInfo.typeOfAgentEmail === typeOfAgentEmail.vehicleBoundNoTPI
          ) {
            this.sendAnalyticsForBindComplete(vehicleStateInfo.selectedAgent.fullName);
          }
        }
      }),
      map(([vehicleStateInfo, primaryEmail, myaccountemail, isPartner]) => {
        const agentEmailType =
          vehicleStateInfo && vehicleStateInfo.typeOfAgentEmail
            ? vehicleStateInfo.typeOfAgentEmail
            : typeOfAgentEmail.expectationSettingAgent;

        if (agentEmailType === 'vehicleBoundNoTPI') {
          this.policyNumberSub = this.store
            .select(addVehicleInfoQuery.selectNewVehicleInfoState)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .subscribe((vehicleInfo: any) => {
              this.policyNumber =
                vehicleInfo.addVehicleInfoState.entities[
                  vehicleInfo?.addVehicleInfoState?.ids
                ].policyNumber;
            });
          this.vehicleNameSub = this.store
            .select(vehicleQuery.selectNewRiskDescription)
            .subscribe(vehicleName => {
              this.vehicleName = vehicleName;
            });
          if (!isPartner) {
            localStorage.setItem('vehicleName', this.vehicleName);
          }
        }

        if (this.classicOnlyAutoPolicyOrCompOnlyAdv) {
          return {
            confirmation: {
              status: this.copyService.getCopy(
                `policy.vehicleInfoSentToYourAgentSuccessStatusText`
              ),
              heading: this.copyService.getCopy(
                `policy.vehicleInfoSentToYourAgentSuccessHeaderText`
              )
            },
            nextSteps: {
              isNote: true,
              heading: 'Next steps',
              text: this.copyService.getCopy(
                `policy.vehicleInfoSentToYourAgentSuccessNextStepText`,
                {
                  primaryEmail: _get(
                    primaryEmail,
                    'emailAddress',
                    _get(myaccountemail, 'emailAddress', 'No Email Found')
                  )
                }
              )
            }
          };
        }

        return {
          confirmation: {
            status: this.copyService.getCopy(
              `policy.${agentEmailType}newVehicleEmailSuccessStatusText`
            ),
            heading: this.copyService.getCopy(
              `policy.${agentEmailType}newVehicleAgentEmailNotificationHeaderText`
            )
          },
          disclaimer: this.copyService.getCopy(`policy.${agentEmailType}DisclaimerConfimation`),
          nextSteps: {
            heading: agentEmailType === 'vehicleBoundNoTPI' ? '' : 'Your next steps',
            text: this.copyService.getCopy(
              `policy.${agentEmailType}newVehicleAgentEmailNotificationBody`,
              {
                primaryEmail: _get(
                  primaryEmail,
                  'emailAddress',
                  _get(myaccountemail, 'emailAddress', 'No Email Found')
                )
              }
            )
          },
          isPartner: isPartner,
          policyNumberText: this.copyService.getCopy('policy.policyNumberText'),
          vehicleAddedText: this.copyService.getCopy('policy.vehicleAddedText'),
          alertMessageConfirmation: this.copyService.getCopy('policy.alertMessageConfirmation'),
          titleConfirmation: this.copyService.getCopy('policy.titleConfirmation')
        };
      })
    );

  private stop$: Subject<void> = new Subject<void>();
  private policyNumberSub: Subscription;
  private vehicleNameSub: Subscription;
  private policySub: Subscription;
  constructor(
    private store: Store,
    private copyService: CopyService,
    private spinnerService: LoadingSpinnerService,
    private vehicleFacade: VehicleFacade,
    private featureFlagService: FeatureFlagService,
    private route: ActivatedRoute,
    private dockingBarService: DockingBarService,
    private analyticsFacade: AnalyticsFacade
  ) {}

  ngOnInit(): void {
    this.store
      .select(
        createSelector(
          addVehicleFeatureQuery.selectAllActiveAgents,
          agentQuery.getAreAllAgentsLoaded,
          addVehicleFeatureQuery.selectAddVehicleOtherExitFlowStatus,
          addVehicleFeatureQuery.selectAddVehicleExitFlowStatus,
          (agents, loaded, otherExitFlow, exitFlow) => ({ agents, loaded, otherExitFlow, exitFlow })
        )
      )
      .pipe(
        filter(data => !!data.loaded),
        take(1)
      )
      .subscribe(data => {
        this.agents = data.agents;
        this.newExperience = data.otherExitFlow || data.exitFlow ? true : false;
        if (data.otherExitFlow === true) {
          this.store.dispatch(
            VehicleInfoActions.addVehicleSendAnalytics({
              payload: { pageAnalyticName: 'Other:SendToAgent' }
            })
          );
        }
      });

    this.spinnerService.stop();

    this.route.params.pipe(takeUntil(this.stop$)).subscribe(params => {
      if (params && params['id'] > 0) {
        this.isNewVehicle = false;
        this.dockingBarService.registerHeading('Replace Vehicle');
      }
    });

    this.policySub = combineLatest([
      this.store.select(BrandSelectors.selectIsPartner),
      this.store.select(PolicySelectors.selectHasClassicPolicies),
      this.store.select(PolicySelectors.selectHasAdvancePolicies),
      this.store.select(PolicySelectors.selectHasOnlyPolicyComprehensiveCoverage)
    ]).subscribe(
      ([isPartner, hasClassicPolicy, advancePolicy, hasOnlyPolicyComprehensiveCoverage]) => {
        const addVehicleSkipHardstop = this.featureFlagService.isEnabled(
          'add_vehicle_skip_hardstop'
        );
        const isShowhardstopRiskSelectionPage =
          this.featureFlagService.isEnabled('add_vehicle') &&
          addVehicleSkipHardstop &&
          advancePolicy &&
          !isPartner &&
          hasOnlyPolicyComprehensiveCoverage;

        const classicOnlyAutoPolicy =
          this.featureFlagService.isEnabled('add_vehicle') &&
          hasClassicPolicy &&
          !advancePolicy &&
          !isPartner &&
          addVehicleSkipHardstop;

        if (isShowhardstopRiskSelectionPage || classicOnlyAutoPolicy) {
          this.classicOnlyAutoPolicyOrCompOnlyAdv = true;
        }
        //}
      }
    );
  }

  ngAfterViewInit() {
    combineLatest([
      this.store.select(addVehicleBindQuery.selectVehicleBindError),
      this.store.select(routerQuery.getRouterPreviousStateUrl)
    ])
      .pipe(
        take(1),
        tap(([bindError, prevStateUrl]) => {
          if (bindError) {
            this.dispatchAnalytics('Error:SentToAgent');
          } else if (prevStateUrl?.toLowerCase().includes('add-vehicle/expectation')) {
            this.dispatchAnalytics('PreStartChecklist:SentToAgent');
          } else if (prevStateUrl?.toLowerCase().includes('add-vehicle/quote-info')) {
            this.dispatchAnalytics('CustomizeCoverage:SentToAgent');
          } else if (
            prevStateUrl?.toLowerCase().includes('add-vehicle/quote-summary') ||
            prevStateUrl?.toLowerCase().includes('add-vehicle/vehicle-quote-summary')
          ) {
            this.dispatchAnalytics('QuoteSummary:SentToAgent');
          } else if (prevStateUrl?.toLowerCase().includes('add-vehicle/vehicle-future-payments')) {
            this.dispatchAnalytics('FuturePaymentSchedule:SentToAgent');
          } else if (
            prevStateUrl?.toLowerCase().includes('add-vehicle/agent-help') &&
            this.classicOnlyAutoPolicyOrCompOnlyAdv
          ) {
            setTimeout(() => {
              this.analyticsFacade.trackPage(PolicyAnalytics.addVehicleClassicPolicySendToAgent);
              // this.store.dispatch(
              //   VehicleInfoActions.addVehicleHardStopSendAnalytics({
              //     payload: { pageAnalyticName: 'AddVehicle:ClassicPolicy:SentToAgent' }
              //   })
              // );
            });
          }
        })
      )
      .subscribe();
  }

  done() {
    this.vehicleFacade.vehiclePolicyChangeEntryPoint$.pipe(take(1)).subscribe(path => {
      this.store.dispatch(
        fromRouterActions.Go({
          path: [path || '/overview']
        })
      );
    });
  }

  ngOnDestroy() {
    if (this.policyNumberSub) {
      this.policyNumberSub.unsubscribe();
    }

    if (this.vehicleNameSub) {
      this.vehicleNameSub.unsubscribe();
    }

    if (this.policySub) {
      this.policySub.unsubscribe();
    }
  }

  private dispatchAnalytics(pageAnalyticName: string, eventName?: string) {
    setTimeout(() => {
      this.store.dispatch(
        VehicleInfoActions.addVehicleSendAnalytics({
          payload: { pageAnalyticName, eventName }
        })
      );
    });
  }

  private sendAnalyticsForBindComplete(agentName: string) {
    this.store.dispatch(
      VehicleInfoActions.addVehicleSendAnalytics({
        payload: { pageAnalyticName: 'Complete', eventName: 'bind complete', agentName: agentName }
      })
    );
  }
}
